import React, { useState, useEffect } from 'react';
import { Plus, Edit2, Trash2, GripVertical, X } from 'lucide-react';
import { getServices } from '../../services/api';
import { Dialog, DialogContent, DialogTitle } from '../../components/ui/dialog';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ServicesAdmin = () => {
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingService, setEditingService] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    duration: '',
    price: '',
    price_note: '',
    category: 'manicure',
    image_url: '',
    is_popular: false,
    is_active: true
  });

  const categories = [
    { id: 'manicure', name: 'Manicures' },
    { id: 'extensions', name: 'Extensions' },
    { id: 'art', name: 'Nail Art' },
    { id: 'other', name: 'Other' }
  ];

  const fetchServices = async () => {
    try {
      const data = await getServices(null, false);
      setServices(data);
    } catch (error) {
      console.error('Failed to fetch services:', error);
      toast.error('Failed to load services');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchServices();
  }, []);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      duration: '',
      price: '',
      price_note: '',
      category: 'manicure',
      image_url: '',
      is_popular: false,
      is_active: true
    });
    setEditingService(null);
  };

  const handleEdit = (service) => {
    setEditingService(service);
    setFormData({
      name: service.name,
      description: service.description || '',
      duration: service.duration,
      price: service.price.toString(),
      price_note: service.price_note || '',
      category: service.category,
      image_url: service.image_url || '',
      is_popular: service.is_popular,
      is_active: service.is_active
    });
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const serviceData = {
      ...formData,
      price: parseFloat(formData.price),
      price_note: formData.price_note || null,
      description: formData.description || null,
      image_url: formData.image_url || null
    };

    try {
      if (editingService) {
        await axios.patch(`${BACKEND_URL}/api/services/${editingService.id}`, serviceData);
        toast.success('Service updated');
      } else {
        await axios.post(`${BACKEND_URL}/api/services`, serviceData);
        toast.success('Service created');
      }
      fetchServices();
      setShowModal(false);
      resetForm();
    } catch (error) {
      toast.error('Failed to save service');
    }
  };

  const handleDelete = async (serviceId) => {
    if (!window.confirm('Are you sure you want to delete this service?')) return;
    
    try {
      await axios.delete(`${BACKEND_URL}/api/services/${serviceId}`);
      setServices(prev => prev.filter(s => s.id !== serviceId));
      toast.success('Service deleted');
    } catch (error) {
      toast.error('Failed to delete service');
    }
  };

  const toggleActive = async (service) => {
    try {
      await axios.patch(`${BACKEND_URL}/api/services/${service.id}`, {
        is_active: !service.is_active
      });
      setServices(prev => prev.map(s => 
        s.id === service.id ? { ...s, is_active: !s.is_active } : s
      ));
      toast.success(service.is_active ? 'Service deactivated' : 'Service activated');
    } catch (error) {
      toast.error('Failed to update service');
    }
  };

  if (loading) {
    return (
      <div className="space-y-4">
        <div className="h-8 w-48 skeleton rounded-lg" />
        {[1,2,3,4].map(i => <div key={i} className="h-20 skeleton rounded-xl" />)}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="heading-1 mb-1">Services</h1>
          <p className="body-small text-[var(--text-secondary)]">{services.length} services</p>
        </div>
        <button
          onClick={() => { resetForm(); setShowModal(true); }}
          className="btn-primary !py-2 !px-4 !min-w-0"
        >
          <Plus size={18} />
          Add Service
        </button>
      </div>

      {/* Services List */}
      <div className="space-y-3">
        {services.map((service) => (
          <div 
            key={service.id} 
            className={`bg-white p-4 rounded-xl flex items-center gap-4 ${!service.is_active ? 'opacity-60' : ''}`}
          >
            {service.image_url && (
              <img 
                src={service.image_url} 
                alt={service.name}
                className="w-16 h-16 rounded-lg object-cover flex-shrink-0"
              />
            )}
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1">
                <h3 className="font-medium text-[var(--text-heading)] truncate">{service.name}</h3>
                {service.is_popular && (
                  <span className="px-2 py-0.5 text-xs bg-[var(--accent-blush)] rounded-full">Popular</span>
                )}
                {!service.is_active && (
                  <span className="px-2 py-0.5 text-xs bg-gray-200 rounded-full">Inactive</span>
                )}
              </div>
              <p className="body-small text-[var(--text-secondary)]">
                {service.duration} • ${service.price}{service.price_note ? ` ${service.price_note}` : ''}
              </p>
              <p className="body-small text-[var(--text-light)] capitalize">{service.category}</p>
            </div>
            <div className="flex items-center gap-1">
              <button
                onClick={() => toggleActive(service)}
                className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                  service.is_active 
                    ? 'bg-green-100 text-green-700 hover:bg-green-200' 
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                {service.is_active ? 'Active' : 'Inactive'}
              </button>
              <button
                onClick={() => handleEdit(service)}
                className="p-2 rounded-lg hover:bg-[var(--bg-subtle)] transition-colors text-[var(--text-secondary)]"
              >
                <Edit2 size={18} />
              </button>
              <button
                onClick={() => handleDelete(service.id)}
                className="p-2 rounded-lg hover:bg-red-50 transition-colors text-red-500"
              >
                <Trash2 size={18} />
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* Add/Edit Modal */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="max-w-lg bg-white rounded-2xl max-h-[90vh] overflow-y-auto">
          <DialogTitle className="heading-2 mb-4">
            {editingService ? 'Edit Service' : 'Add New Service'}
          </DialogTitle>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block body-small font-medium mb-1">Name *</label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                required
                className="w-full px-4 py-2.5 bg-[var(--bg-subtle)] border border-[var(--border-light)] rounded-xl focus:border-[var(--accent-blush-dark)] focus:outline-none"
              />
            </div>
            <div>
              <label className="block body-small font-medium mb-1">Description</label>
              <textarea
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                rows={2}
                className="w-full px-4 py-2.5 bg-[var(--bg-subtle)] border border-[var(--border-light)] rounded-xl focus:border-[var(--accent-blush-dark)] focus:outline-none resize-none"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block body-small font-medium mb-1">Duration *</label>
                <input
                  type="text"
                  name="duration"
                  value={formData.duration}
                  onChange={handleInputChange}
                  required
                  placeholder="60 min"
                  className="w-full px-4 py-2.5 bg-[var(--bg-subtle)] border border-[var(--border-light)] rounded-xl focus:border-[var(--accent-blush-dark)] focus:outline-none"
                />
              </div>
              <div>
                <label className="block body-small font-medium mb-1">Price *</label>
                <input
                  type="number"
                  name="price"
                  value={formData.price}
                  onChange={handleInputChange}
                  required
                  step="0.01"
                  className="w-full px-4 py-2.5 bg-[var(--bg-subtle)] border border-[var(--border-light)] rounded-xl focus:border-[var(--accent-blush-dark)] focus:outline-none"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block body-small font-medium mb-1">Price Note</label>
                <input
                  type="text"
                  name="price_note"
                  value={formData.price_note}
                  onChange={handleInputChange}
                  placeholder="per nail"
                  className="w-full px-4 py-2.5 bg-[var(--bg-subtle)] border border-[var(--border-light)] rounded-xl focus:border-[var(--accent-blush-dark)] focus:outline-none"
                />
              </div>
              <div>
                <label className="block body-small font-medium mb-1">Category *</label>
                <select
                  name="category"
                  value={formData.category}
                  onChange={handleInputChange}
                  className="w-full px-4 py-2.5 bg-[var(--bg-subtle)] border border-[var(--border-light)] rounded-xl focus:border-[var(--accent-blush-dark)] focus:outline-none"
                >
                  {categories.map(cat => (
                    <option key={cat.id} value={cat.id}>{cat.name}</option>
                  ))}
                </select>
              </div>
            </div>
            <div>
              <label className="block body-small font-medium mb-1">Image URL</label>
              <input
                type="url"
                name="image_url"
                value={formData.image_url}
                onChange={handleInputChange}
                placeholder="https://..."
                className="w-full px-4 py-2.5 bg-[var(--bg-subtle)] border border-[var(--border-light)] rounded-xl focus:border-[var(--accent-blush-dark)] focus:outline-none"
              />
            </div>
            <div className="flex items-center gap-6">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  name="is_popular"
                  checked={formData.is_popular}
                  onChange={handleInputChange}
                  className="w-4 h-4 rounded"
                />
                <span className="body-small">Mark as Popular</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  name="is_active"
                  checked={formData.is_active}
                  onChange={handleInputChange}
                  className="w-4 h-4 rounded"
                />
                <span className="body-small">Active</span>
              </label>
            </div>
            <div className="flex justify-end gap-3 pt-4">
              <button
                type="button"
                onClick={() => setShowModal(false)}
                className="px-4 py-2 text-[var(--text-secondary)] hover:text-[var(--text-heading)] transition-colors"
              >
                Cancel
              </button>
              <button type="submit" className="btn-primary !py-2 !px-6 !min-w-0">
                {editingService ? 'Update' : 'Create'}
              </button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ServicesAdmin;
